import {
  Alert,
  alpha,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Popper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import DataObjectIcon from "@mui/icons-material/DataObject";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { FormEvent, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import logo from "../../assets/logo.png";
import logoLight from "../../assets/logo-pos.png";
import { useCheckPermissaoSegura } from "../../hooks/useCheckPermissaoSegura";
import {
  SinterConsultaDivergencia,
  SinterConsultaImovel,
  SinterConsultaSituacao,
  SinterConsultaTipoBusca,
} from "../../interfaces";
import { ColorModeContext } from "../../providers/Theme";
import {
  getSinterConsultaImovelDetalhe,
  getSinterConsultaImoveis,
  getSinterConsultaStats,
  getTenantConfig,
} from "../../services/api";
import { useMunicipioFromUrl } from "../../hooks/useMunicipioFromUrl";
import { hasIntegracaoSinter } from "../../utils/integracaoSinter";
import { DialogVersion } from "../Dashboard/DialogVersion";
import LoadingPage from "../LoadingPage";
import pj from "../../../package.json";

type ResultTab = "resumo" | "comparacao" | "detalhamento";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 650;
const SINTER_QUERY_GC_TIME_MS = 30 * 60 * 1000;

const sinterNavigationCacheOptions = {
  gcTime: SINTER_QUERY_GC_TIME_MS,
  staleTime: SINTER_QUERY_GC_TIME_MS,
  refetchOnMount: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
} as const;

const searchTypeOptions: Array<{ value: SinterConsultaTipoBusca; label: string; placeholder: string; maxLength?: number; inputMode?: "numeric" | "text" }> = [
  { value: "quadra", label: "Quadra", placeholder: "Informe a inscrição cartográfica a nível de quadra", maxLength: 8, inputMode: "numeric" },
  { value: "lote", label: "Lote", placeholder: "Informe a inscrição cartográfica a nível de lote", maxLength: 12, inputMode: "numeric" },
  { value: "inscricao_cartografica", label: "Inscrição cartográfica", placeholder: "Informe a inscrição cartográfica a nível de unidade", maxLength: 15, inputMode: "numeric" },
  { value: "cib", label: "CIB", placeholder: "Informe o CIB", maxLength: 8, inputMode: "text" },
];

const situationOptions: Array<{ value: SinterConsultaSituacao; label: string }> = [
  { value: "todos", label: "Todos" },
  { value: "consistentes", label: "Consistentes" },
  { value: "divergentes", label: "Divergentes" },
  { value: "pendentes", label: "Pendentes" },
];

const transientQueryParams = ["x-tn-token", "x-auth-token", "municipio", "tipo_busca", "inscricao_cartografica"];

function isSinterConsultaTipoBusca(value: string | null): value is SinterConsultaTipoBusca {
  return searchTypeOptions.some((option) => option.value === value);
}

function normalizeSearchInputValue(value: string, tipoBusca: SinterConsultaTipoBusca) {
  const config = searchTypeOptions.find((item) => item.value === tipoBusca);
  const normalized = tipoBusca === "cib" ? value.toUpperCase().replace(/[^A-Z0-9]/g, "") : value.replace(/\D/g, "");
  return config?.maxLength ? normalized.slice(0, config.maxLength) : normalized;
}

function clearTransientQueryParams() {
  const params = new URLSearchParams(window.location.search);
  const shouldClean = transientQueryParams.some((param) => params.has(param));

  if (!shouldClean) {
    return;
  }

  transientQueryParams.forEach((param) => params.delete(param));

  const queryString = params.toString();
  const nextUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ""}${window.location.hash}`;

  window.history.replaceState(window.history.state, document.title, nextUrl);
}

function pageShellSx(theme: Theme) {
  return {
    minHeight: "100vh",
    pb: 8,
    ...(theme.palette.mode === "dark"
      ? { bgcolor: "background.default" }
      : { background: "linear-gradient(180deg, #f8fafc 0%, #f3f5f7 48%, #f3f5f7 100%)" }),
  };
}

function surfaceSx(theme: Theme, elevated = false) {
  const isDark = theme.palette.mode === "dark";
  return {
    borderRadius: 3,
    border: `1px solid ${isDark ? alpha(theme.palette.divider, 0.12) : alpha(theme.palette.divider, 0.1)}`,
    bgcolor: isDark ? theme.palette.background.paper : alpha(theme.palette.background.paper, 0.92),
    boxShadow: elevated
      ? isDark
        ? "0 18px 55px rgba(0, 0, 0, 0.22)"
        : "0 1px 2px rgba(15, 23, 42, 0.04), 0 16px 44px rgba(15, 23, 42, 0.06)"
      : "none",
  };
}

function inputFieldSx(theme: Theme) {
  const isDark = theme.palette.mode === "dark";
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.5,
      bgcolor: isDark ? alpha(theme.palette.common.white, 0.035) : alpha(theme.palette.background.default, 0.6),
    },
  };
}

function HeaderShell(props: { onBack: () => void; onOpenAuditoria: () => void }) {
  const theme = useTheme();
  const color = useContext(ColorModeContext);

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        borderBottom: `1px solid ${theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.06) : alpha(theme.palette.divider, 0.08)}`,
        bgcolor: theme.palette.mode === "dark" ? alpha(theme.palette.background.default, 0.9) : alpha(theme.palette.background.paper, 0.86),
        backdropFilter: "blur(12px)",
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: 2, sm: 3 }, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
          <img className="logo-dashboard" src={theme.palette.mode === "dark" ? logo : logoLight} alt="logo" style={{ height: 32, opacity: 0.92 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap display="block">
              Caracterização
            </Typography>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ lineHeight: 1.3 }}>
              Consulta SINTER
            </Typography>
            <DialogVersion>
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                v{pj.version}
              </Typography>
            </DialogVersion>
          </Box>
        </Box>

        <Stack direction="row" gap={0.5} alignItems="center">
          <Button
            size="small"
            variant="text"
            startIcon={<FactCheckOutlinedIcon fontSize="small" />}
            onClick={props.onOpenAuditoria}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              display: { xs: "none", sm: "inline-flex" },
              height: 36,
              px: 1.6,
              color: theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.text.primary,
              "&:hover": {
                bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.08 : 0.04),
              },
            }}
          >
            Auditoria
          </Button>
          <Tooltip title="Voltar">
            <IconButton size="small" onClick={props.onBack} sx={{ color: "text.secondary" }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={color.toggleColorMode} sx={{ color: "text.secondary" }}>
            {theme.palette.mode === "dark" ? <Brightness7Icon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );
}

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "Não informado";
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasRecordData(value: unknown): value is Record<string, unknown> {
  return isRecord(value) && Object.keys(value).length > 0;
}

function humanizeKey(key: string): string {
  const labels: Record<string, string> = {
    cib: "CIB",
    Cib: "CIB",
    cpf: "CPF",
    cnpj: "CNPJ",
    cep: "CEP",
    uf: "UF",
    siglaUf: "UF",
    siglaUd: "UF",
    DadosGeraisImovel: "Dados gerais do imóvel",
    EnderecoImovel: "Endereço do imóvel",
    AreaConstruidaCompl: "Área construída complementar",
    InfoIbge: "Informações IBGE",
    Titular: "Titularidade",
    ServicoRegistroImovel: "Serviço de registro de imóvel",
    CartorioNotas: "Cartório de notas",
    ITBI: "ITBI",
    inscricaoImobiliaria: "Inscrição imobiliária",
    inscricao_cartografica: "Inscrição cartográfica",
    id_imobiliario: "ID do imóvel",
  };

  if (labels[key]) return labels[key];

  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

function situacaoLabel(situacao?: string) {
  if (situacao === "consistente") return "Consistente";
  if (situacao === "divergente") return "Divergente";
  if (situacao === "pendente") return "Pendente";
  return "—";
}

function situacaoColor(situacao?: string): "success" | "warning" | "default" {
  if (situacao === "consistente") return "success";
  if (situacao === "divergente") return "warning";
  return "default";
}

function StatsCard(props: {
  label: string;
  value: number;
  tooltip: string;
  icon: ReactNode;
  selected: boolean;
  accent: "default" | "success" | "warning" | "pending";
  onClick: () => void;
}) {
  const theme = useTheme();
  const colors = {
    default: theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.primary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    pending: theme.palette.mode === "dark" ? theme.palette.grey[300] : theme.palette.text.secondary,
  };
  const color = colors[props.accent];
  const selectedBgAlpha = theme.palette.mode === "dark" ? 0.24 : 0.1;
  const selectedBorderAlpha = theme.palette.mode === "dark" ? 0.76 : 0.55;

  return (
    <Tooltip title={props.tooltip} arrow>
      <Box
        onClick={props.onClick}
        role="button"
        tabIndex={0}
        sx={{
          flex: 1,
          minWidth: { xs: "100%", sm: 180 },
          px: 2.25,
          py: 2,
          cursor: "pointer",
          borderRadius: 2,
          border: `1px solid ${props.selected ? alpha(color, selectedBorderAlpha) : alpha(theme.palette.divider, 0.14)}`,
          bgcolor: props.selected ? alpha(color, selectedBgAlpha) : alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.4 : 0.86),
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {props.label}
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, color, lineHeight: 1.15 }}>
              {props.value}
            </Typography>
          </Box>
          <Box sx={{ color, opacity: 0.9 }}>{props.icon}</Box>
        </Stack>
      </Box>
    </Tooltip>
  );
}

function PayloadValue({ value }: { value: unknown }) {
  if (Array.isArray(value)) {
    if (!value.length) return <Typography variant="body2">Não informado</Typography>;
    return (
      <Stack gap={1}>
        {value.map((item, index) => (
          <Box key={index} sx={{ pl: 1.5, borderLeft: "2px solid", borderColor: "divider" }}>
            {isRecord(item) ? <PayloadFields value={item} compact /> : <Typography variant="body2">{formatValue(item)}</Typography>}
          </Box>
        ))}
      </Stack>
    );
  }

  if (isRecord(value)) {
    return <PayloadFields value={value} compact />;
  }

  return <Typography variant="body2">{formatValue(value)}</Typography>;
}

function PayloadFields({ value, compact = false }: { value: Record<string, unknown>; compact?: boolean }) {
  const theme = useTheme();
  const entries = Object.entries(value).filter(([, item]) => item !== undefined);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: compact ? "1fr" : { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
        gap: compact ? 0.75 : 1,
      }}
    >
      {entries.map(([key, item]) => (
        <Box
          key={key}
          sx={{
            p: compact ? 0 : 1.25,
            borderRadius: 1.5,
            border: compact ? 0 : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            minWidth: 0,
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
            {humanizeKey(key)}
          </Typography>
          <Box sx={{ mt: 0.35, wordBreak: "break-word" }}>
            <PayloadValue value={item} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function PayloadDetails({ title, value, accent = "primary" }: { title: string; value: unknown; accent?: "primary" | "success" }) {
  const theme = useTheme();
  const accentColor = accent === "success" ? theme.palette.success.main : theme.palette.primary.main;

  if (!isRecord(value)) {
    return (
      <Accordion defaultExpanded disableGutters sx={{ borderRadius: 2, border: `1px solid ${alpha(accentColor, 0.28)}`, overflow: "hidden", "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, px: 2, py: 0.25, bgcolor: alpha(accentColor, theme.palette.mode === "dark" ? 0.18 : 0.09), borderBottom: `1px solid ${alpha(accentColor, 0.2)}`, "& .MuiAccordionSummary-content": { my: 0.75 } }}>
          <Typography variant="subtitle2" fontWeight={800}>
            {title}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Sem informação.
          </Typography>
        </AccordionDetails>
      </Accordion>
    );
  }

  const entries = Object.entries(value).filter(([, item]) => item !== undefined);

  return (
    <Accordion defaultExpanded disableGutters sx={{ borderRadius: 2, border: `1px solid ${alpha(accentColor, 0.28)}`, overflow: "hidden", bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.22 : 0.95), "&:before": { display: "none" } }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 2,
          py: 0.5,
          bgcolor: alpha(accentColor, theme.palette.mode === "dark" ? 0.18 : 0.09),
          borderBottom: `1px solid ${alpha(accentColor, 0.2)}`,
          minHeight: 40,
          "& .MuiAccordionSummary-content": { my: 0.75 },
        }}
      >
        <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.25 }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2 }}>
      <Stack gap={1.5}>
        {entries.map(([section, sectionValue]) => (
          <Accordion key={section} defaultExpanded disableGutters sx={{ border: `1px solid ${alpha(accentColor, 0.16)}`, borderRadius: 1.5, overflow: "hidden", "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, bgcolor: alpha(accentColor, theme.palette.mode === "dark" ? 0.08 : 0.04), "& .MuiAccordionSummary-content": { my: 0.7 } }}>
              <Typography variant="body2" fontWeight={750} sx={{ lineHeight: 1.25 }}>
                {humanizeKey(section)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 1.5 }}>
            {isRecord(sectionValue) ? <PayloadFields value={sectionValue} /> : <PayloadValue value={sectionValue} />}
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
function ComparisonTable({ rows }: { rows: SinterConsultaDivergencia[] }) {
  const theme = useTheme();
  const groupedRows = useMemo(() => {
    const groups = new Map<string, SinterConsultaDivergencia[]>();
    rows.forEach((row) => {
      const group = row.grupo || "Dados gerais";
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group)!.push(row);
    });
    return Array.from(groups.entries());
  }, [rows]);

  if (!rows.length) {
    return (
      <Box sx={{ py: 5, textAlign: "center", borderRadius: 2, border: `1px dashed ${alpha(theme.palette.divider, 0.22)}` }}>
        <CheckCircleOutlineIcon sx={{ color: "success.main", mb: 1 }} />
        <Typography variant="body2" fontWeight={600}>
          Nenhuma divergência cadastral identificada.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack gap={2}>
      {groupedRows.map(([group, groupRows]) => (
        <Box key={group} sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.12)}`, overflow: "hidden" }}>
          <Box
            sx={{
              px: 2,
              py: 1.4,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.16 : 0.07),
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.25 }}>
              {group}
            </Typography>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Campo", "Geo360", "SINTER", "Status"].map((col) => (
                    <TableCell key={col} sx={{ fontWeight: 700, color: "text.secondary" }}>
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {groupRows.map((row) => (
                  <TableRow key={`${row.campo}-${row.campo_sinter}`}>
                    <TableCell sx={{ fontWeight: 700 }}>{row.label}</TableCell>
                    <TableCell>{formatValue(row.valor_geo360)}</TableCell>
                    <TableCell>{formatValue(row.valor_sinter)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.status === "consistente_por_dominio" ? "Consistente por domínio" : row.status === "divergente" ? "Divergente" : "Consistente"}
                        color={row.status === "divergente" ? "warning" : "success"}
                        variant="outlined"
                        sx={{ borderRadius: 1.5, fontWeight: 600 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Stack>
  );
}
export default function SinterConsultaPage() {
  const autorizado = useCheckPermissaoSegura();
  const theme = useTheme();
  const navigate = useNavigate();
  const { municipio } = useMunicipioFromUrl();
  const initialSearchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const requestedTipoBusca = initialSearchParams.get("tipo_busca");
  const initialTipoBusca: SinterConsultaTipoBusca = isSinterConsultaTipoBusca(requestedTipoBusca)
    ? requestedTipoBusca
    : "inscricao_cartografica";
  const initialSearchValue = normalizeSearchInputValue(
    initialSearchParams.get(initialTipoBusca) ?? initialSearchParams.get("q") ?? "",
    initialTipoBusca,
  );

  useEffect(() => {
    if (autorizado) {
      clearTransientQueryParams();
    }
  }, [autorizado]);

  const [searchValue, setSearchValue] = useState(initialSearchValue);
  const [appliedSearch, setAppliedSearch] = useState(initialSearchValue);
  const [tipoBusca, setTipoBusca] = useState<SinterConsultaTipoBusca>(initialTipoBusca);
  const [appliedTipoBusca, setAppliedTipoBusca] = useState<SinterConsultaTipoBusca>(initialTipoBusca);
  const [situacao, setSituacao] = useState<SinterConsultaSituacao>("todos");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<SinterConsultaImovel | null>(null);
  const [resultTab, setResultTab] = useState<ResultTab>("resumo");
  const [showCacheNotice, setShowCacheNotice] = useState(false);
  const [cacheNoticeAnchorEl, setCacheNoticeAnchorEl] = useState<HTMLButtonElement | null>(null);
  const tipoBuscaRef = useRef(tipoBusca);
  tipoBuscaRef.current = tipoBusca;

  const { data: integracaoDisponivel, isLoading: loadingIntegracao } = useQuery({
    queryKey: ["sinter-consulta-integracao-disponivel", municipio],
    queryFn: async () => {
      const config = await getTenantConfig();
      return hasIntegracaoSinter(config);
    },
    ...sinterNavigationCacheOptions,
  });

  const statsParams = useMemo(() => ({ q: appliedSearch, tipo_busca: appliedTipoBusca }), [appliedSearch, appliedTipoBusca]);
  const listParams = useMemo(
    () => ({ ...statsParams, situacao, page: page + 1, page_size: PAGE_SIZE }),
    [statsParams, situacao, page],
  );

  const statsQuery = useQuery({
    queryKey: ["sinter-consulta-stats", municipio, statsParams],
    queryFn: () => getSinterConsultaStats(statsParams),
    enabled: integracaoDisponivel === true,
    ...sinterNavigationCacheOptions,
  });

  const imoveisQuery = useQuery({
    queryKey: ["sinter-consulta-imoveis", municipio, listParams],
    queryFn: () => getSinterConsultaImoveis(listParams),
    enabled: integracaoDisponivel === true,
    ...sinterNavigationCacheOptions,
  });

  const detalheQuery = useMutation({
    mutationFn: ({ id, tipoBusca }: { id: number; tipoBusca: SinterConsultaTipoBusca }) =>
      getSinterConsultaImovelDetalhe(id, { tipo_busca: tipoBusca }),
    retry: false,
  });

  function handleVoltarDashboard() {
    const loaded = localStorage.getItem("loaded");
    const idLote = loaded ? JSON.parse(loaded) : null;
    if (idLote) {
      navigate(`/dashboard/${idLote}`);
      return;
    }
    navigate("/");
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = searchValue.trim();
    setAppliedSearch(trimmed);
    setAppliedTipoBusca(tipoBusca);
    setPage(0);
    setSelected(null);
  }

  useEffect(() => {
    const trimmed = searchValue.trim();

    if (!trimmed) {
      setAppliedSearch("");
      setAppliedTipoBusca(tipoBuscaRef.current);
      setPage(0);
      setSelected(null);
      return;
    }

    const timer = window.setTimeout(() => {
      setAppliedSearch(trimmed);
      setAppliedTipoBusca(tipoBuscaRef.current);
      setPage(0);
      setSelected(null);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchValue]);

  function handleClear() {
    setSearchValue("");
    setAppliedSearch("");
    setAppliedTipoBusca(tipoBusca);
    setPage(0);
    setSelected(null);
  }

  function normalizeSearchInput(value: string, nextTipoBusca = tipoBusca) {
    return normalizeSearchInputValue(value, nextTipoBusca);
  }

  function applySituacao(next: SinterConsultaSituacao) {
    setSituacao(next);
    setPage(0);
    setSelected(null);
  }

  const searchConfig = searchTypeOptions.find((item) => item.value === tipoBusca) ?? searchTypeOptions[2];
  const rows = imoveisQuery.data?.results ?? [];
  const activeDetail = detalheQuery.data?.id_imobiliario === selected?.id_imobiliario ? detalheQuery.data : undefined;
  const detalheLoading = detalheQuery.isPending;
  const detalheSemRetorno = !detalheLoading && !detalheQuery.isError && Boolean(selected) && !activeDetail;
  const sinterSemPayload = !detalheLoading && Boolean(activeDetail) && !hasRecordData(activeDetail?.payload_sinter);
  const comparisonRows = activeDetail?.comparacao ?? [];
  const totalRows = statsQuery.data
    ? {
        todos: statsQuery.data.todos,
        consistentes: statsQuery.data.consistentes,
        divergentes: statsQuery.data.divergentes,
        pendentes: statsQuery.data.pendentes,
      }[situacao]
    : imoveisQuery.data?.total_records ?? 0;

  if (!autorizado) return <LoadingPage message="Verificando permissões de acesso" />;
  if (loadingIntegracao) return <LoadingPage message="Verificando integração SINTER" />;

  if (integracaoDisponivel === false) {
    return (
      <Box sx={{ ...pageShellSx(theme), display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
        <Box sx={{ maxWidth: 420, width: "100%", p: 4, borderRadius: 3, textAlign: "center", ...surfaceSx(theme, true) }}>
          <FactCheckOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Consulta indisponível
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            A integração SINTER não está habilitada para o município atual.
          </Typography>
          <Button variant="contained" disableElevation onClick={handleVoltarDashboard} sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 500 }}>
            Voltar ao dashboard
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={pageShellSx(theme)}>
      <HeaderShell onBack={handleVoltarDashboard} onOpenAuditoria={() => navigate("/sinter/auditoria")} />

      <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: 2, sm: 3 }, pt: { xs: 3, sm: 5 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.4, fontWeight: 700 }}>
            Integração SINTER
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{ mt: 0.35, lineHeight: 1.15, fontSize: { xs: "1.65rem", sm: "2rem" } }}>
            Consulta de imóvel
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25, maxWidth: 720, lineHeight: 1.7 }}>
            Consulte a situação dos imóveis enviados ao SINTER e compare os dados cadastrais com o Geo360.
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", md: "row" }} gap={1.5} sx={{ mb: 3 }}>
          <StatsCard
            label="Todos"
            value={statsQuery.data?.todos ?? 0}
            tooltip="Exibe todas as inscrições existentes na base Geo360."
            icon={<HomeWorkOutlinedIcon />}
            selected={situacao === "todos"}
            accent="default"
            onClick={() => applySituacao("todos")}
          />
          <StatsCard
            label="Consistentes"
            value={statsQuery.data?.consistentes ?? 0}
            tooltip="Inscrições comparadas com o SINTER e sem divergências cadastrais identificadas."
            icon={<CheckCircleOutlineIcon />}
            selected={situacao === "consistentes"}
            accent="success"
            onClick={() => applySituacao("consistentes")}
          />
          <StatsCard
            label="Divergentes"
            value={statsQuery.data?.divergentes ?? 0}
            tooltip="Inscrições que apresentam diferenças entre os dados do Geo360 e do SINTER."
            icon={<WarningAmberOutlinedIcon />}
            selected={situacao === "divergentes"}
            accent="warning"
            onClick={() => applySituacao("divergentes")}
          />
          <StatsCard
            label="Pendentes"
            value={statsQuery.data?.pendentes ?? 0}
            tooltip="Inscrições que ainda não foram enviadas ao SINTER ou possuem pendências que impedem o envio."
            icon={<HourglassEmptyIcon />}
            selected={situacao === "pendentes"}
            accent="pending"
            onClick={() => applySituacao("pendentes")}
          />
        </Stack>

        {false && showCacheNotice ? (
          <Alert
          icon={<WarningAmberOutlinedIcon fontSize="small" />}
          variant="outlined"
          action={
            <Tooltip title="Fechar aviso">
              <IconButton size="small" onClick={() => setShowCacheNotice(false)} sx={{ color: "text.secondary" }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
          sx={{
            mb: 3,
            borderRadius: 2.5,
            color: "text.primary",
            alignItems: "center",
            borderColor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.42 : 0.36),
            bgcolor: theme.palette.mode === "dark" ? alpha(theme.palette.warning.main, 0.08) : alpha(theme.palette.warning.main, 0.055),
            "& .MuiAlert-icon": {
              color: "warning.main",
            },
            "& .MuiAlert-action": {
              alignItems: "center",
              pt: 0,
            },
          }}
        >
          A listagem, as buscas e os contadores utilizam os dados locais atualizados diariamente. A consulta ao SINTER em tempo real ocorre somente ao clicar em um imóvel.
        </Alert>
        ) : false && (
          <Box sx={{ mb: 3 }}>
            <Button
              size="small"
              variant="text"
              startIcon={<WarningAmberOutlinedIcon fontSize="small" />}
              onClick={() => setShowCacheNotice(true)}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                color: "warning.dark",
                px: 1,
                "&:hover": {
                  bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.12 : 0.08),
                },
              }}
            >
              Ver aviso sobre atualização
            </Button>
          </Box>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, ...surfaceSx(theme, true) }}>
          <Stack direction={{ xs: "column", md: "row" }} gap={1} alignItems="stretch">
            <TextField
              label="Buscar imóvel"
              placeholder={searchConfig.placeholder}
              value={searchValue}
              onChange={(event) => setSearchValue(normalizeSearchInput(event.target.value))}
              fullWidth
              size="small"
              sx={{ ...inputFieldSx(theme), flex: 1 }}
              inputProps={{
                maxLength: searchConfig.maxLength,
                inputMode: searchConfig.inputMode,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchValue ? (
                  <InputAdornment position="end">
                    <Tooltip title="Limpar">
                      <IconButton size="small" onClick={handleClear}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <TextField
              select
              label="Tipo de busca"
              value={tipoBusca}
              onChange={(event) => {
                const nextTipoBusca = event.target.value as SinterConsultaTipoBusca;
                setTipoBusca(nextTipoBusca);
                setSearchValue((current) => normalizeSearchInput(current, nextTipoBusca));
                setPage(0);
              }}
              size="small"
              sx={{ ...inputFieldSx(theme), width: { xs: "100%", md: 230 } }}
            >
              {searchTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Situação"
              value={situacao}
              onChange={(event) => applySituacao(event.target.value as SinterConsultaSituacao)}
              size="small"
              sx={{ ...inputFieldSx(theme), width: { xs: "100%", md: 210 } }}
            >
              {situationOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Button type="submit" variant="contained" disableElevation startIcon={<ManageSearchIcon />} sx={{ height: 40, borderRadius: 2.5, textTransform: "none", fontWeight: 700, px: 2.5 }}>
              Buscar
            </Button>
          </Stack>
        </Box>

        {(statsQuery.isError || imoveisQuery.isError) && (
          <Alert severity="error" variant="outlined" sx={{ mb: 3, borderRadius: 2.5 }}>
            Não foi possível carregar a consulta SINTER. Tente novamente em instantes.
          </Alert>
        )}

        <Box sx={{ ...surfaceSx(theme, true), overflow: "hidden", mb: 3, position: "relative" }}>
          <Tooltip title="Sobre a atualização da listagem">
            <IconButton
              size="small"
              onClick={(event) => {
                if (showCacheNotice) {
                  setShowCacheNotice(false);
                  setCacheNoticeAnchorEl(null);
                  return;
                }

                setCacheNoticeAnchorEl(event.currentTarget);
                setShowCacheNotice(true);
              }}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 1,
                color: showCacheNotice ? "warning.main" : "text.secondary",
                bgcolor: theme.palette.mode === "dark" ? alpha(theme.palette.background.paper, 0.9) : alpha(theme.palette.background.paper, 0.94),
                "&:hover": {
                  bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.12 : 0.08),
                },
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {false && showCacheNotice && (
            <Alert
              icon={<InfoOutlinedIcon fontSize="small" />}
              variant="outlined"
              action={
                <Tooltip title="Fechar aviso">
                  <IconButton size="small" onClick={() => setShowCacheNotice(false)} sx={{ color: "text.secondary" }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
              sx={{
                mx: 2,
                mb: 1.5,
                borderRadius: 2,
                color: "text.primary",
                borderColor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.42 : 0.34),
                bgcolor: theme.palette.mode === "dark" ? alpha(theme.palette.warning.main, 0.08) : alpha(theme.palette.warning.main, 0.055),
                "& .MuiAlert-icon": {
                  color: "warning.main",
                },
                "& .MuiAlert-action": {
                  alignItems: "center",
                  pt: 0,
                },
              }}
            >
              A listagem, as buscas e os contadores utilizam os dados locais atualizados diariamente. A consulta ao SINTER em tempo real ocorre somente ao clicar em um imóvel.
            </Alert>
          )}

          {imoveisQuery.isLoading || imoveisQuery.isFetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {["Inscrição Cartográfica", "CIB", "Endereço", "Proprietário", "Status SINTER", "Última sincronização", "Situação"].map((col) => (
                        <TableCell key={col} sx={{ py: 1.5, pr: col === "Situação" ? 5 : undefined, color: "text.secondary", fontWeight: 700, fontSize: "0.76rem" }}>
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 7, border: 0 }}>
                          <ManageSearchIcon sx={{ color: "text.disabled", mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            Nenhum imóvel encontrado para os filtros informados.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => {
                        const isSelectedRow = selected?.id_imobiliario === row.id_imobiliario;
                        const rowView = row;

                        return (
                          <TableRow
                            key={row.id_imobiliario}
                            hover
                            selected={isSelectedRow}
                            onClick={() => {
                              setSelected(row);
                              setResultTab("resumo");
                              detalheQuery.mutate({ id: row.id_imobiliario, tipoBusca: appliedTipoBusca });
                            }}
                            sx={{
                              cursor: "pointer",
                              "&:last-child td": { border: 0 },
                              "&.Mui-selected": {
                                bgcolor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.2 : 0.08),
                              },
                              "&.Mui-selected:hover": {
                                bgcolor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.26 : 0.12),
                              },
                            }}
                          >
                            <TableCell sx={{ fontFamily: "monospace", fontWeight: 700 }}>{rowView.inscricao_cartografica}</TableCell>
                            <TableCell>{rowView.cib || "—"}</TableCell>
                            <TableCell>{rowView.endereco || "—"}</TableCell>
                            <TableCell>{rowView.proprietario || "—"}</TableCell>
                            <TableCell>{rowView.status_sinter || "—"}</TableCell>
                            <TableCell>{formatDateTime(rowView.ultima_sincronizacao)}</TableCell>
                            <TableCell>
                              <Tooltip title={rowView.motivo_pendencia || (rowView.divergencias.length ? `${rowView.divergencias.length} divergência(s)` : "")}>
                                <Chip size="small" label={situacaoLabel(rowView.situacao)} color={situacaoColor(rowView.situacao)} variant={rowView.situacao === "pendente" ? "outlined" : "filled"} sx={{ borderRadius: 1.5, fontWeight: 700 }} />
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`, px: 1 }}>
                <TablePagination
                component="div"
                count={totalRows}
                page={page}
                onPageChange={(_, nextPage) => {
                  setPage(nextPage);
                  setSelected(null);
                }}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                labelRowsPerPage="Por página"
                sx={{ borderTop: 0 }}
              />
              </Box>
              <Popper
                open={showCacheNotice && Boolean(cacheNoticeAnchorEl)}
                anchorEl={cacheNoticeAnchorEl}
                placement="left-start"
                modifiers={[
                  { name: "offset", options: { offset: [0, 8] } },
                  { name: "preventOverflow", options: { padding: 12 } },
                ]}
                sx={{ zIndex: theme.zIndex.modal }}
              >
                <Box
                  sx={{
                    maxWidth: 420,
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.16 : 0.12)}`,
                    bgcolor: theme.palette.background.paper,
                    boxShadow: theme.palette.mode === "dark" ? "0 18px 45px rgba(0,0,0,0.42)" : "0 14px 34px rgba(15, 23, 42, 0.16)",
                  }}
                >
                <Stack direction="row" gap={1.25} alignItems="flex-start">
                  <InfoOutlinedIcon fontSize="small" sx={{ color: "warning.main", mt: 0.2 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                      Atualização da listagem
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                      A listagem, as buscas e os contadores utilizam os dados locais atualizados diariamente. A consulta ao SINTER em tempo real ocorre somente ao clicar em um imóvel.
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setShowCacheNotice(false);
                      setCacheNoticeAnchorEl(null);
                    }}
                    sx={{ mt: -0.5, mr: -0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
                </Box>
              </Popper>
            </>
          )}
        </Box>

        {selected && (
          <Box sx={{ ...surfaceSx(theme, true), overflow: "hidden" }}>
            <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={1}>
                <Tabs
                  value={resultTab}
                  onChange={(_, value: ResultTab) => setResultTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 44,
                    "& .MuiTabs-indicator": { bgcolor: "success.main" },
                    "& .MuiTab-root.Mui-selected": { color: "success.main" },
                  }}
                >
                  <Tab value="resumo" icon={<FactCheckOutlinedIcon fontSize="small" />} iconPosition="start" label="Resumo" sx={{ minHeight: 44, textTransform: "none", fontWeight: 700 }} />
                  <Tab value="comparacao" icon={<CompareArrowsOutlinedIcon fontSize="small" />} iconPosition="start" label="Comparação" sx={{ minHeight: 44, textTransform: "none", fontWeight: 700 }} />
                  <Tab value="detalhamento" icon={<DataObjectIcon fontSize="small" />} iconPosition="start" label="Detalhamento" sx={{ minHeight: 44, textTransform: "none", fontWeight: 700 }} />
                </Tabs>
              </Stack>
            </Box>

            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              {detalheLoading && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, py: 6, borderRadius: 2, border: `1px dashed ${alpha(theme.palette.divider, 0.22)}` }}>
                  <CircularProgress size={30} />
                  <Typography variant="body2" color="text.secondary" fontWeight={700}>
                    Buscando dados no SINTER...
                  </Typography>
                </Box>
              )}

              {!detalheLoading && detalheQuery.isError && (
                <Alert severity="warning" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                  {"N\u00e3o foi poss\u00edvel consultar os dados do SINTER para este im\u00f3vel. Tente novamente em instantes."}
                </Alert>
              )}

              {!detalheLoading && detalheSemRetorno && (
                <Alert severity="warning" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                  {"O SINTER n\u00e3o retornou dados para este im\u00f3vel."}
                </Alert>
              )}

              {!detalheLoading && sinterSemPayload && (
                <Alert severity="warning" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                  {"O SINTER n\u00e3o retornou dados para este im\u00f3vel."}
                </Alert>
              )}

              {!detalheLoading && resultTab === "resumo" && (
                <Stack gap={2}>
                  <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                    {[
                      ["Inscrição cartográfica", selected.inscricao_cartografica],
                      ["CIB", activeDetail?.cib || selected.cib || "—"],
                      ["Situação", situacaoLabel(activeDetail?.situacao ?? selected.situacao)],
                      ["Última sincronização", formatDateTime(activeDetail?.ultima_sincronizacao ?? selected.ultima_sincronizacao)],
                    ].map(([label, value]) => (
                      <Box key={label} sx={{ flex: 1, minWidth: 160, p: 2, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                          {label}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                  {(activeDetail?.motivo_pendencia || selected.motivo_pendencia) && <Alert severity="warning" sx={{ borderRadius: 2 }}>{activeDetail?.motivo_pendencia || selected.motivo_pendencia}</Alert>}
                </Stack>
              )}

              {!detalheLoading && resultTab === "comparacao" && <ComparisonTable rows={comparisonRows} />}

              {!detalheLoading && resultTab === "detalhamento" && (
                <Stack gap={2.5}>
                  <PayloadDetails title="Geo360" value={activeDetail?.payload_geo360} accent="success" />
                  <PayloadDetails title="SINTER" value={activeDetail?.payload_sinter} accent="primary" />
                </Stack>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
